import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel, IconCreate } from '../icons/HvacIcons';
import './hv-modal.css';

export interface AddedNode {
  id: string;
  deviceId: string;
  parentNodeId: string | null;
  symbol: string;
  displayName: string | null;
  category: string | null;
  sortOrder: number;
}

interface Props {
  plantId: string;
  /** Parent context. Either a deviceId (root-level node) or a parentNodeId
   *  (nested node). Exactly one is set. */
  parent: { deviceId: string; parentNodeId: string | null; parentLabel: string };
  onCreated(node: AddedNode): void;
  onClose(): void;
}

/** POST /api/plants/{plantId}/nodes form. Symbol is required and immutable
 *  after creation; the rest is optional. */
export function AddNodeModal({ plantId, parent, onCreated, onClose }: Props) {
  const [symbol, setSymbol] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('10');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const sym = symbol.trim();
      if (sym === '') throw new Error('Symbol darf nicht leer sein.');
      const so = Number(sortOrder);
      if (!Number.isFinite(so) || !Number.isInteger(so)) throw new Error('Sortierung muss eine ganze Zahl sein.');
      const body: Record<string, unknown> = {
        symbol: sym,
        sortOrder: so,
      };
      if (parent.parentNodeId) body.parentNodeId = parent.parentNodeId;
      else body.deviceId = parent.deviceId;
      const dn = displayName.trim();
      if (dn !== '') body.displayName = dn;
      const cat = category.trim();
      if (cat !== '') body.category = cat;
      const res = await api.post<AddedNode>(`/api/plants/${plantId}/nodes`, body);
      onCreated(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Anlegen fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Neuer Node</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Übergeordnet</label>
            <input type="text" value={parent.parentLabel} disabled />
          </div>
          <div class="hv-modal__field">
            <label>Symbol *</label>
            <input type="text" value={symbol} onInput={(e) => setSymbol((e.target as HTMLInputElement).value)} placeholder="z.B. heizkreis1" />
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename</label>
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} placeholder="optional" />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Kategorie</label>
              <input type="text" value={category} onInput={(e) => setCategory((e.target as HTMLInputElement).value)} placeholder="optional" />
            </div>
            <div class="hv-modal__field">
              <label>Sortierung</label>
              <input type="text" value={sortOrder} onInput={(e) => setSortOrder((e.target as HTMLInputElement).value)} />
            </div>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <div style={{ flex: 1 }} />
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCreate size={14} />Anlegen</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
